import { PAYMENT_METHOD_ORDER } from 'src/payments/paymentMethods'
import { paypalCurrencySupported } from 'src/payments/paypalCurrency'

// Payment method registry. Each key carries its compatibility
// constraints; methods drop off the available list when those constraints
// stop holding (and reappear when they hold again). Employee wallet is
// deliberately omitted — it's handled outside the chooser today. Crypto,
// stock, and DAF all live in the chooser, even though they route to
// their own step components downstream rather than reusing the fiat
// steps.
//
// `oneTimeOnly`   — only available when subscription === 'one_time'
// `usdOnly`       — only available when currency.iso_code === 'USD'
// `paypalCurrency` — only available when the selected currency is one PayPal
//   settles; the list is server-supplied via paypalProps.currencies. Venmo
//   doesn't carry it — its `usdOnly` is a strict subset.
// `usReceiptOnly` — only available when neither Canadian receipt nor
//   Gift Aid is enabled. The settlement pipeline for these methods
//   (DonateStock / Chariot / future DonateStock-DAF) issues US-only
//   charitable receipts; the donor-side jurisdiction tax credits don't
//   apply. Distinct from `usdOnly` — currency vs. receipt jurisdiction.
//   See project_payment_method_tax_credits memory.
// `singleBeneficiaryOnly` — only available when the donor has selected
//   a single nonprofit (not a split donation / multi-org). The
//   third-party SDKs init with a specific EIN and can't fan out to
//   multiple recipients, so split donation has nowhere to send the
//   funds.
// `requiresApplePay` / `requiresGooglePay` — only available when the
//   discovery ECE has confirmed the device supports that wallet. The
//   underlying source of truth is useExpressCheckoutDiscovery; the
//   App-level filter passes the resolved booleans into this filter so
//   AP and GP rows pop in once Stripe reports them ready.

export const PAYMENT_METHODS = {
  apple_pay:   { requiresApplePay:  true },
  google_pay:  { requiresGooglePay: true },
  credit_card: {},
  bank:        { usdOnly: true },
  paypal:      { paypalCurrency: true },
  venmo:       { oneTimeOnly: true, usdOnly: true },
  crypto:      { oneTimeOnly: true },
  wire:        { oneTimeOnly: true, usdOnly: true },
  check:       { oneTimeOnly: true, usdOnly: true },
  pledge:      {},
  stock:       { oneTimeOnly: true, usdOnly: true, usReceiptOnly: true, singleBeneficiaryOnly: true },
  daf:         { oneTimeOnly: true, usdOnly: true, usReceiptOnly: true, singleBeneficiaryOnly: true },
}

// Stable display order for the chooser — lives in the cross-surface
// registry (src/payments/paymentMethods, imported above; shared with
// the Impact Hub checkout preview so the two can't drift); re-exported
// here so widget-internal imports keep their local path.
export { PAYMENT_METHOD_ORDER }

// The backend currently bundles wire/check/pledge into a single
// `deferred` entry (matches v6's sub-tab layout). v7 flattens these
// into the top-level chooser, so expand the bundle here. TODO: once the
// backend serializer can emit them separately for v7 forms, drop this.
const expandBundles = methods =>
  methods.flatMap(m => m === 'deferred' ? [ 'wire', 'check', 'pledge' ] : [ m ])

// Apple Pay / Google Pay are not in the partner-configured payment
// methods list (the backend doesn't know about wallet availability,
// only Stripe in the donor's browser does). They're injected into the
// available set whenever the partner has Stripe configured (i.e. the
// credit_card method is in the partner's configured list) AND the
// discovery ECE has confirmed availability. Caller passes
// `applePayAvailable` / `googlePayAvailable` from the discovery hook.
export const filterPaymentMethods = (methods, {
  subscription, currency, applePayAvailable, googlePayAvailable,
  canadianReceiptEnabled, giftAidEnabled, multipleBeneficiaries,
  paypalCurrencies,
}) => {
  const expanded = new Set(expandBundles(methods || []))
  // Stripe-configured partners are the ones eligible for AP/GP rows.
  // Use credit_card presence as the proxy for "Stripe is wired up."
  if (expanded.has('credit_card')) {
    if (applePayAvailable)  expanded.add('apple_pay')
    if (googlePayAvailable) expanded.add('google_pay')
  }
  return PAYMENT_METHOD_ORDER.filter(method => {
    if (!expanded.has(method)) return false
    const config = PAYMENT_METHODS[method]
    if (config.oneTimeOnly && subscription !== 'one_time') return false
    if (config.usdOnly && currency?.iso_code !== 'USD') return false
    if (config.paypalCurrency &&
        !paypalCurrencySupported(currency?.iso_code, paypalCurrencies)) return false
    if (config.usReceiptOnly && (canadianReceiptEnabled || giftAidEnabled)) return false
    if (config.singleBeneficiaryOnly && multipleBeneficiaries) return false
    return true
  })
}
